import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import {
  filter,
  get,
  isEmpty,
  sortBy,
  keys,
  values,
  head,
  isArray,
  toNumber,
  find,
  uniq,
  forEach,
  includes,
} from 'lodash';
import { useTranslation } from 'react-i18next';
import { format } from 'date-fns';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  AppBar,
  Box,
  CircularProgress,
  Grid2,
  Tab,
  Tabs,
  Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  findDocuments,
  findPatients,
  getBookmarksDocumentCount,
  getPatientInformation,
} from './_api';
import { downloadPdfDocument, getPdfDocument } from '../Patient/_api';
import { getClinicalPortalSetting } from 'modules/Administration/ClinicalPortal/_api';
import { getUser } from 'modules/Administration/Users/_api';
import { findGridConfiguration } from 'modules/Administration/FrontendSettings/GridConfigurations/_api';
import { IGridConfiguration } from 'modules/Administration/FrontendSettings/GridConfigurations/_types';
import useAlerts from 'components/Alerts/useAlerts';
import Header from 'components/Header/Header';
import { Papeer } from 'components/Papeer/Papeer';
import { useAppInfo } from 'utils/hooks/useAppInfo';
import { useLanguage } from 'utils/hooks/useLanguage';
import { joinParams } from 'utils/study';
import { useEntityInfo } from 'utils/hooks/useEntityInfo';
import { useAppGlobals } from 'utils/hooks/useAppGlobals';
import { apiDataToDownloadFile, printOrDownloadBase64String } from 'utils/hooks/useApp';
import { useStudy } from 'utils/hooks/useStudy';
import { getFirstActiveProduct, getSystemSetting } from 'utils/products';
import { DocumentGrid } from './DocumentGrid';
import {
  IBookmark,
  IBookmarkDefaultColumns,
  IFieldForPatient,
  IMaxCount,
  IPatient,
  IPatientDocument,
  IPatientInformation,
  ITypeOfDocument,
} from '../Patient/_types';
import { useActions } from 'utils/hooks/useActions';
import { useStorage } from 'utils/hooks/useStorage';
import { useUser } from 'utils/hooks/useUser';
import {
  DATA_FOR_DOCUMENT_DETAIL_FROM_STORAGE,
  TARGET_GRID_DOCUMENTATION,
} from 'constants/constants';

const formatDate = 'dd. MM. yyyy';

const sxDisplayPrintNone = {
  '@media print': {
    display: 'none',
  },
};

const OTHER_DATA_KEY = 'patientDetailOther';
const EXPANDED_VALUE = true;
const EXPANDED_EXTENDED_VALUE = false;

export const ClinicalPortalByPatientDetailFhir: React.FC = () => {
  const { t } = useTranslation('ClinicalPortal');
  const navigate = useNavigate();
  const { toggleLoader } = useAppGlobals();
  const { storeClinicalPortalSetting, storeFindDocuments } = useActions();
  const { user } = useUser();
  const { id } = useEntityInfo();
  const { addErrorAlert } = useAlerts();
  const { selectedDocuments, patients, clinicalPortalSettingFromRedux, clinicalPortalProducts } =
    useAppInfo();
  const { currentLocale } = useLanguage();
  const { linkBack } = useStudy();
  const { getItem, putItem, removeItem } = useStorage();

  const backFromDetail = linkBack();
  const backUrl = '/clinicPortal/fhir/byPatient?action=back';

  const [bookmarks, setBookmarks] = useState<IBookmark[]>([]);
  const [activeTab, setActiveTab] = useState<string>('');
  const [maxCounts, setMaxCounts] = useState<IMaxCount>({
    maxExportDocumentsCount: 16,
    maxViewDocumentsCount: 16,
  });
  const [isPrinting, setIsPrinting] = useState<boolean>(false);
  const [isExporting, setIsExporting] = useState<boolean>(false);
  const [standardizedPrint, setStandardizedPrint] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [expanded, setExpanded] = useState<boolean>(EXPANDED_VALUE);
  const [expandedExtended, setExpandedExtended] = useState<boolean>(EXPANDED_EXTENDED_VALUE);

  const [entity, setEntity] = useState<IPatient | null>(null);
  const [patientInformation, setPatientInformation] = useState<IPatientInformation | null>(null);
  const [patientDocuments, setPatientDocuments] = useState<{
    [activeTab: string]: IPatientDocument[];
  }>({});
  const [countOfPatientDocuments, setCountOfPatientDocuments] = useState<{
    [activeTab: string]: number;
  }>({});
  const [fieldsForPatientName, setFieldsForPatientName] = useState<IFieldForPatient[]>([]);
  const [fieldsForPatientInfo, setFieldsForPatientInfo] = useState<IFieldForPatient[]>([]);
  const [fieldsForPatientExtendedInfo, setFieldsForPatientExtendedInfo] = useState<
    IFieldForPatient[]
  >([]);
  const [RenderedTab, setRenderedTab] = useState<JSX.Element[]>([]);
  const [gridConfiguration, setGridConfiguration] = useState<null | IGridConfiguration>(null);

  const getFields = (entity: IPatient, patientInformation: IPatientInformation | null = null) => {
    const fieldsForPatientName = [
      {
        name: 'name',
        value: `${joinParams([
          get(entity, 'prefix'),
          get(entity, 'lastName'),
          get(entity, 'firstName'),
          get(entity, 'middleName'),
          get(entity, 'suffix'),
        ])}
      `,
        lg: 6,
      },
    ];
    setFieldsForPatientName(fieldsForPatientName);

    const fieldsForPatientInfo = [
      {
        name: 'patientIdentificationNumber',
        label: t('patientIdentificationNumber'),
        value: `${
          get(entity, 'identificationNumber', '-') != null
            ? get(entity, 'identificationNumber', '-')
            : '-'
        }`,
      },
      {
        name: 'patientID',
        label: t('patientInsuranceNumber'),
        value: `${get(entity, 'insuranceNumber', '-') || '-'}`,
      },
      {
        name: 'patientBirthDate',
        label: t('patientBirthDate'),
        value: get(entity, 'birthDate', '')
          ? format(new Date(get(entity, 'birthDate', '')), formatDate)
          : '-',
      },
      {
        name: 'patientSex',
        label: t('patientSex'),
        value: t(`patient_sex_${get(entity, 'sex', 'O')}`),
      },
      {
        name: 'address',
        label: t('address'),
        value: joinParams(
          [
            get(entity, 'address', ''),
            joinParams([get(entity, 'postcode', ''), get(entity, 'town', '')]),
          ],
          ', ',
        ),
      },
      {
        name: 'telephone',
        label: t('telephone'),
        value: `${
          isArray(get(entity, 'telephone', '')) || isArray(get(patientInformation, 'phones', ''))
            ? joinParams(
                uniq([
                  ...(get(entity, 'telephone', []) as any[]),
                  ...(get(patientInformation, 'phones', []) as any[]),
                ]),
                ', ',
              )
            : ''
        }`,
      },
      {
        name: 'motherMaidenName',
        label: t('birthLastName'),
        value: `${get(entity, 'birthLastName', '') || ''}`,
      },
      {
        name: 'masterPatientID',
        label: t('masterPatientID'),
        value: `${get(entity, 'masterPatientID', '') || ''}`,
      },
    ];
    setFieldsForPatientInfo(fieldsForPatientInfo);

    const fieldsForPatientExtendedInfo = [
      {
        name: 'patientInsurance',
        label: t('patientInsurance'),
        value: `${
          patientInformation
            ? isArray(get(patientInformation, 'insuranceNumberCodes', ''))
              ? joinParams(get(patientInformation, 'insuranceNumberCodes', []) as any[], ', ')
              : ''
            : '-'
        }`,
      },
      {
        name: 'email',
        label: t('email'),
        value: `${
          isArray(get(entity, 'email', '')) || isArray(get(patientInformation, 'mails', ''))
            ? joinParams(
                uniq([
                  ...(get(entity, 'email', []) as any[]),
                  ...(get(patientInformation, 'mails', []) as any[]),
                ]),
                ', ',
              )
            : '-'
        }`,
      },
      {
        name: 'occupation',
        label: t('occupation'),
        value: `${get(patientInformation, 'employment', '-') || '-'}`,
      },
      {
        name: 'attendingGeneralPractitioner',
        label: t('attendingGeneralPractitioner'),
        value: `${get(patientInformation, 'generalPractitioner', '-') || '-'}`,
      },
      {
        name: 'bloodGroup',
        label: t('bloodGroup'),
        value: `${
          patientInformation
            ? isArray(get(patientInformation, 'bloodGroups', ''))
              ? joinParams(get(patientInformation, 'bloodGroups', []) as any[], ', ')
              : ''
            : '-'
        }`,
      },
      {
        name: 'allergy',
        label: t('allergy'),
        value: `${
          patientInformation
            ? isArray(get(patientInformation, 'allergies', ''))
              ? joinParams(get(patientInformation, 'allergies', []) as any[], ', ')
              : ''
            : '-'
        }`,
      },
    ];
    setFieldsForPatientExtendedInfo(fieldsForPatientExtendedInfo);
  };

  const getPatientInfo = async (entity: IPatient | null) => {
    const patientInformation = await getPatientInformation(id.toString());
    if (entity && patientInformation) {
      setPatientInformation(patientInformation);
      getFields(entity, patientInformation);
    }
  };

  const toggleExpanded = () => {
    putToStorage(!expanded, expandedExtended, activeTab);
    setExpanded(!expanded);
  };
  const toggleExpandedExtended = async () => {
    if (!expandedExtended && !patientInformation) {
      getPatientInfo(entity);
    }
    putToStorage(expanded, !expandedExtended, activeTab);
    setExpandedExtended(!expandedExtended);
  };
  const toggleActiveTab = (activeTab: string) => {
    putToStorage(expanded, expandedExtended, activeTab);
    setActiveTab(activeTab);
  };

  const getDocuments = async (
    masterPatientID: string,
    activeTab: string,
    showLoading: boolean = true,
  ) => {
    if (showLoading) setIsLoading(true);
    let allDocuments: IPatientDocument[] = [];
    const documents: IPatientDocument[] = await findDocuments(masterPatientID, activeTab);
    if (documents) {
      const preparedDocuments = documents.map((item, index: number) => ({
        ...item,
        index,
        uuidex: `${item.uuid}_${index}`,
        cannotBySelected: get(item, 'typeOfDocument.name', '') === 'DICOM',
        showDocumentIcon: 'Search',
        typeOfDocumentName: get(item, 'typeOfDocument.translateJson')
          ? get(
              JSON.parse(get(item, 'typeOfDocument.translateJson')),
              currentLocale,
              get(item, 'typeOfDocument.name'),
            )
          : '?',
      }));
      allDocuments = [...preparedDocuments];
      setPatientDocuments({ ...patientDocuments, [activeTab]: allDocuments });
    }
    if (showLoading) setIsLoading(false);
    return allDocuments;
  };

  const getCountOfBookmarksDocuments = async () => {
    const allCountOfPatientDocuments = await getBookmarksDocumentCount(id.toString());
    setCountOfPatientDocuments(allCountOfPatientDocuments);
  };

  const loadEntities = async () => {
    toggleLoader(true);
    removeItem(DATA_FOR_DOCUMENT_DETAIL_FROM_STORAGE);
    storeFindDocuments([]);
    let clinicalPortalSetting = clinicalPortalSettingFromRedux;
    if (!clinicalPortalSetting) {
      clinicalPortalSetting = await getClinicalPortalSetting();
      storeClinicalPortalSetting(clinicalPortalSetting);
    }

    const [userFromDb, gridConfiguration] = await Promise.all([
      getUser(user.sub),
      findGridConfiguration(TARGET_GRID_DOCUMENTATION),
    ]);
    const usersWorkplaces = (get(userFromDb, 'workPlaces', []) || []).map((item) =>
      get(item, 'code', ''),
    );
    setGridConfiguration(gridConfiguration);

    let activeTab = '';
    let bookmarks: any[] = [];
    if (clinicalPortalSetting) {
      bookmarks = sortBy(filter(get(clinicalPortalSetting, 'bookmarks', []), { enabled: true }), [
        'position',
      ]);
    }

    let allowedBookmarks: any[] = [];
    bookmarks.forEach((bookmark) => {
      forEach(bookmark.allowedWorkplaces, (item) => {
        if (includes(usersWorkplaces, item.code)) {
          allowedBookmarks.push(bookmark);
        }
      });
    });
    bookmarks = uniq(allowedBookmarks).map((bookmark) => {
      const bookmarkNames = JSON.parse(get(bookmark, 'name', null));
      const aKeys = keys(bookmarkNames);
      const zValues = values(bookmarkNames);
      let title = '';
      aKeys.forEach((key, i) => {
        if (key === currentLocale && zValues[i] !== '') {
          title = zValues[i];
        }
        if (title === '' && zValues[i] !== '') {
          title = zValues[i];
        }
      });
      return { ...bookmark, title };
    });
    setBookmarks(bookmarks);
    const first = head(bookmarks);
    if (first !== undefined) {
      activeTab = get(first, 'id', '');
      setActiveTab(activeTab);
    }

    const masterPatientID = id.toString();
    let patient = find(patients, { masterPatientID });

    // If patient is not in the store, try to get it from the API with masterPatientID (id)
    if (!patient && masterPatientID) {
      const patientsFromApi = await findPatients({ masterPatientID });
      patient = isArray(patientsFromApi) ? find(patientsFromApi, { masterPatientID }) : undefined;
    }

    if (!patient) {
      addErrorAlert(t('getDataError'));
      toggleLoader(false);
      navigate(backUrl);
      return;
    }
    getFields(patient);
    setEntity(patient);

    const productsByType = clinicalPortalProducts;
    if (isArray(productsByType)) {
      const product = getFirstActiveProduct(productsByType);

      if (product) {
        const systemSettings = get(product, 'systemSetting', []);
        const maxExportDocumentsCount = get(
          getSystemSetting(systemSettings, 'variable', 'maxExportDocumentsCount'),
          'value',
        );
        setMaxCounts({
          ...maxCounts,
          maxExportDocumentsCount: maxExportDocumentsCount ? toNumber(maxExportDocumentsCount) : 5,
        });
      }
    }
    if (backFromDetail) {
      const otherData = getItem(OTHER_DATA_KEY);
      if (otherData) {
        // Parse it to a javaScript object
        try {
          const parsedOtherData = JSON.parse(otherData);
          const storageExpanded = get(parsedOtherData, 'expanded', EXPANDED_VALUE);
          const storageExpandedExtended = get(
            parsedOtherData,
            'expandedExtended',
            EXPANDED_EXTENDED_VALUE,
          );
          const storageActiveTab = get(parsedOtherData, 'activeTab', get(first, 'id', ''));
          setActiveTab(storageActiveTab);
          setExpanded(storageExpanded);
          if (storageExpandedExtended && !patientInformation) {
            await getPatientInfo(patient);
          }
          putToStorage(storageExpanded, storageExpandedExtended, storageActiveTab);
          setExpandedExtended(storageExpandedExtended);
        } catch (err) {
          console.log(err);
        }
      }
    } else {
      putToStorage(
        EXPANDED_VALUE,
        EXPANDED_EXTENDED_VALUE,
        first !== undefined ? get(first, 'id', '') : '',
      );
    }

    toggleLoader(false);

    // Data for first tab
    getDocuments(id.toString(), activeTab);

    getCountOfBookmarksDocuments();
  };

  useEffect(() => {
    loadEntities();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const getDocumentsForActiveTab = async () => {
    const RenderedTab = [];
    if (entity !== null && bookmarks !== null) {
      const bookmark = find(bookmarks, { id: activeTab });
      const allowedTypesOfDocument = get(bookmark, 'allowedTypesOfDocument', null) as
        | ITypeOfDocument[]
        | null;
      const bookmarkDefaultColumns = get(bookmark, 'bookmarkDefaultColumns', null) as
        | IBookmarkDefaultColumns[]
        | null;
      let documents = get(patientDocuments, activeTab);
      if (!isEmpty(patientDocuments) && documents === undefined && !isLoading) {
        // Data for other tab
        getDocuments(id.toString(), activeTab);
      }
      storeFindDocuments(documents);
      RenderedTab.push(
        <Box key={activeTab}>
          <DocumentGrid
            masterPatientID={get(entity, 'masterPatientID', '')}
            facilities={[]}
            bookmarkId={activeTab}
            allowedTypesOfDocument={allowedTypesOfDocument}
            bookmarkDefaultColumns={bookmarkDefaultColumns}
            patientDocuments={documents || null}
            patient={entity}
            maxCounts={maxCounts}
            setIsPrinting={setIsPrinting}
            setStandardizedPrint={setStandardizedPrint}
            setIsExporting={setIsExporting}
            isLoading={isLoading}
            gridConfiguration={gridConfiguration}
          />
        </Box>,
      );
      setRenderedTab(RenderedTab);
    }
  };

  useEffect(() => {
    getDocumentsForActiveTab();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, entity, patientDocuments, bookmarks.length, isLoading]);

  const loadDocument = async (
    masterPatientID: string,
    documentUiid: string,
    typeOfDocument: string,
    standardizedPrint: boolean,
  ) => {
    if (masterPatientID !== null && documentUiid !== null && typeOfDocument !== null) {
      const documentPrintInfoResponse = await getPdfDocument(
        masterPatientID,
        documentUiid,
        typeOfDocument,
        standardizedPrint,
      );
      if (documentPrintInfoResponse) {
        return documentPrintInfoResponse;
      }
    }
    return undefined;
  };
  const workWithPdf = async () => {
    toggleLoader();
    for await (const uuid of selectedDocuments) {
      const patientDoc = find(get(patientDocuments, activeTab), { uuid });
      const fileName = get(entity, 'masterPatientID', null) + '.pdf';
      if (isPrinting) {
        const base64PdfString = await loadDocument(
          get(entity, 'masterPatientID', ''),
          uuid,
          get(patientDoc, 'typeOfDocument.name', ''),
          standardizedPrint,
        );
        const contentType = 'application/pdf';
        if (base64PdfString) {
          printOrDownloadBase64String(base64PdfString, contentType, fileName);
        } else {
          addErrorAlert(t('printDocumentError'));
        }
        setStandardizedPrint(false);
        setIsPrinting(false);
      } else if (isExporting) {
        const base64PdfString = await downloadPdfDocument(
          get(entity, 'masterPatientID', null),
          uuid,
          get(patientDoc, 'typeOfDocument.name', ''),
          standardizedPrint,
        );
        if (base64PdfString) {
          apiDataToDownloadFile(base64PdfString, fileName);
        } else {
          addErrorAlert(t('exportDocumentError'));
        }
        setStandardizedPrint(false);
        setIsExporting(false);
      }
    }
    toggleLoader(false);
  };
  useEffect(() => {
    if (isPrinting || isExporting) {
      workWithPdf();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isExporting, isPrinting]);

  const putToStorage = (
    expandedValue: boolean,
    expandedExtendedValue: boolean,
    activeTabValue: string,
  ) => {
    putItem(
      OTHER_DATA_KEY,
      JSON.stringify({
        expanded: expandedValue,
        expandedExtended: expandedExtendedValue,
        activeTab: activeTabValue,
      }),
    );
  };

  return (
    <>
      {entity && fieldsForPatientName && (
        <>
          <Papeer>
            <Header title={t('detail')} backUrl={backUrl} />

            <Box id="patient">
              <Accordion elevation={0} defaultExpanded={expanded} onChange={() => toggleExpanded()}>
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon />}
                  aria-controls="panel-patient"
                  id="panel-patient"
                >
                  <Typography>{get(fieldsForPatientName, '[0].value', '')}</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Grid2 container={true} spacing={1}>
                    {fieldsForPatientInfo.map((field) => (
                      <Grid2 size={{ xs: 6, md: 4, lg: 3 }} {...field.grid} key={field.name}>
                        <Box component="label" sx={{ fontWeight: 'bold' }}>
                          {field.label}
                        </Box>
                        <div>{field.value}</div>
                      </Grid2>
                    ))}
                  </Grid2>
                </AccordionDetails>
              </Accordion>
            </Box>
            {expanded && (
              <Box id="patinetExtendedInfo" sx={sxDisplayPrintNone}>
                <Accordion
                  elevation={0}
                  expanded={expandedExtended}
                  onChange={() => toggleExpandedExtended()}
                >
                  <AccordionSummary
                    expandIcon={<ExpandMoreIcon />}
                    aria-controls="panel-patient-extended"
                    id="panel-patient-extended"
                  >
                    <Grid2 container={true} spacing={1}>
                      <Grid2 size={{ xs: 12, md: 12, lg: 12 }} key={'moreInfo'}>
                        <Typography
                          style={{
                            textAlign: 'justify',
                            textAlignLast: 'right',
                            paddingRight: 18,
                          }}
                        >
                          {t(expandedExtended ? 'lessPatientInfo' : 'morePatientInfo')}
                        </Typography>
                      </Grid2>
                    </Grid2>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Grid2 container={true} spacing={1}>
                      {patientInformation ? (
                        fieldsForPatientExtendedInfo.map((field) => (
                          <Grid2
                            item={true}
                            size={{ xs: 6, md: 4, lg: 3 }}
                            {...field.grid}
                            key={field.name}
                          >
                            <Box component="label" sx={{ fontWeight: 'bold' }}>
                              {field.label}
                            </Box>
                            <div>{field.value}</div>
                          </Grid2>
                        ))
                      ) : (
                        <Grid2 size={{ xs: 12 }}>
                          <Grid2 container={true} justifyContent="center">
                            <CircularProgress size={24} />
                          </Grid2>
                        </Grid2>
                      )}
                    </Grid2>
                  </AccordionDetails>
                </Accordion>
              </Box>
            )}
          </Papeer>

          <Box id="patient" sx={sxDisplayPrintNone}>
            <AppBar position="static" color="default" sx={{ mt: 2 }}>
              <Tabs
                value={activeTab}
                onChange={(event, value) => toggleActiveTab(value)}
                indicatorColor="primary"
                textColor="primary"
                variant="scrollable"
                scrollButtons="auto"
                allowScrollButtonsMobile
              >
                {!isEmpty(bookmarks) &&
                  bookmarks.map((bookmark) => (
                    <Tab
                      value={get(bookmark, 'id')}
                      label={
                        <div>
                          {`${get(bookmark, 'title')} `}
                          {get(countOfPatientDocuments, get(bookmark, 'id')) !== undefined ? (
                            `(${get(countOfPatientDocuments, get(bookmark, 'id'))})`
                          ) : (
                            <CircularProgress size={16} />
                          )}
                        </div>
                      }
                      disabled={get(countOfPatientDocuments, get(bookmark, 'id')) === 0}
                      key={`tab_${get(bookmark, 'id')}`}
                    />
                  ))}
              </Tabs>
            </AppBar>
            <Box sx={{ py: 2 }}>{activeTab !== '' && RenderedTab}</Box>
          </Box>
        </>
      )}
    </>
  );
};
